/**
 * Reimplemented functions from flatpickr.js to support keyboard navigation with allowInput = true
 * https://github.com/flatpickr/flatpickr/issues/1329
 */
import flatpickr from 'flatpickr';

declare type DayElement = HTMLSpanElement & {
  dateObj: Date;
  $i: number;
};

export function handleOnKeyDown(
  instance: flatpickr.Instance,
  event: any,
  options: any
): void {
  if (options.allowInput) {
    handleInputKeyDown(event, instance);
  } else {
    if (event.code === 'Tab') {
      instance.input.focus();
      instance.close();
    }
  }
}

/**
 * Function for onKeyDown listener, handle TAB and TAB + Shift
 * to focus calendar day from inputbox or focus inputbox from calendar.
 * Logic is similar to ARROW UP/DOWN from flatpickr.js
 * https://github.com/flatpickr/flatpickr/blob/master/src/index.ts#L1733
 * @param event
 * @param instance
 */
function handleInputKeyDown(event: any, instance: flatpickr.Instance): void {
  console.log(event);
  if (
    event.key === 'Tab' &&
    event.target === instance.input &&
    !event.shiftKey &&
    instance.isOpen
  ) {
    event.preventDefault();
    const eventTarget = event.target;
    const delta = event.key === 'Tab' ? 1 : -1;
    if (
      (instance.daysContainer && eventTarget.$i !== undefined) ||
      eventTarget === instance.input ||
      eventTarget === instance.altInput
    ) {
      focusOnDay(undefined, delta * 7, instance);
    }
  } else if (event.key === 'Tab' && event.target !== instance.input) {
    instance.input.focus();
    instance.close();
  }
}

function isInView(
  elem: HTMLElement | Element | DayElement,
  instance: flatpickr.Instance
) {
  if (instance.daysContainer !== undefined)
    return (
      elem.className.indexOf('hidden') === -1 &&
      elem.className.indexOf('flatpickr-disabled') === -1 &&
      instance.daysContainer.contains(elem)
    );
  return false;
}

function compareDates(date1: Date, date2: Date, timeless: boolean) {
  if (timeless === void 0) {
    timeless = true;
  }
  if (timeless) {
    return (
      new Date(date1.getTime()).setHours(0, 0, 0, 0) -
      new Date(date2.getTime()).setHours(0, 0, 0, 0)
    );
  }
  return date1.getTime() - date2.getTime();
}

function isEnabled(
  date: Date | string | number,
  timeless: boolean,
  instance: flatpickr.Instance
) {
  let _a;
  if (timeless === void 0) {
    timeless = true;
  }
  const dateToCheck = instance.parseDate(date, undefined, timeless); // timeless
  if (
    (instance.config.minDate &&
      dateToCheck &&
      compareDates(
        dateToCheck,
        instance.config.minDate,
        timeless !== undefined ? timeless : !instance.minDateHasTime
      ) < 0) ||
    (instance.config.maxDate &&
      dateToCheck &&
      compareDates(
        dateToCheck,
        instance.config.maxDate,
        timeless !== undefined ? timeless : !instance.maxDateHasTime
      ) > 0)
  ) {
    return false;
  }
  if (!instance.config.enable && instance.config.disable.length === 0) {
    return true;
  }
  if (dateToCheck === undefined) {
    return false;
  }
  const bool = !!instance.config.enable,
    array =
      (_a = instance.config.enable) !== null && _a !== void 0
        ? _a
        : instance.config.disable;
  for (let i = 0, d: any = void 0; i < array.length; i++) {
    d = array[i];
    if (
      typeof d === 'function' &&
      d(dateToCheck) // disabled by function
    ) {
      return bool;
    } else if (d instanceof Date && d.getTime() === dateToCheck.getTime()) {
      // disabled by date
      return bool;
    } else if (typeof d === 'string') {
      // disabled by date string
      const parsed = instance.parseDate(d, undefined, true);
      return parsed && parsed.getTime() === dateToCheck.getTime()
        ? bool
        : !bool;
    } else if (
      // disabled by range
      typeof d === 'object' &&
      d.from &&
      d.to &&
      dateToCheck.getTime() >= d.from.getTime() &&
      dateToCheck.getTime() <= d.to.getTime()
    ) {
      return bool;
    }
  }
  return !bool;
}

function getFirstAvailableDay(delta: number, instance: flatpickr.Instance) {
  const startMonth = delta > 0 ? 0 : instance.config.showMonths - 1;
  const endMonth = delta > 0 ? instance.config.showMonths : -1;
  for (let m = startMonth; m !== endMonth; m += delta) {
    const month = instance.daysContainer?.children[m];
    if (month) {
      const startIndex = delta > 0 ? 0 : month.children.length - 1;
      const endIndex = delta > 0 ? month.children.length : -1;
      for (let i = startIndex; i !== endIndex; i += delta) {
        const c = month.children[i] as DayElement;
        if (
          c.className.indexOf('hidden') === -1 &&
          isEnabled(c.dateObj, false, instance)
        )
          return c;
      }
    }
  }
  return undefined;
}

function focusOnDayElem(targetNode: DayElement) {
  targetNode.focus();
}

function getNextAvailableDay(
  current: DayElement,
  delta: number,
  instance: flatpickr.Instance
) {
  const givenMonth =
    current.className.indexOf('Month') === -1
      ? current.dateObj.getMonth()
      : instance.currentMonth;
  const endMonth = delta > 0 ? instance.config.showMonths : -1;
  const loopDelta = delta > 0 ? 1 : -1;
  for (
    let m = givenMonth - instance.currentMonth;
    m !== endMonth;
    m += loopDelta
  ) {
    const month = instance.daysContainer?.children[m];
    if (month) {
      let startIndex;
      if (givenMonth - instance.currentMonth === m) {
        startIndex = current.$i + delta;
      } else {
        if (delta < 0) {
          startIndex = month.children.length - 1;
        } else {
          startIndex = 0;
        }
      }
      const numMonthDays = month.children.length;
      for (
        let i = startIndex;
        i >= 0 && i < numMonthDays && i !== (delta > 0 ? numMonthDays : -1);
        i += loopDelta
      ) {
        const c = month.children[i] as DayElement;
        if (
          c.className.indexOf('hidden') === -1 &&
          isEnabled(c.dateObj, false, instance) &&
          Math.abs(current.$i - i) >= Math.abs(delta)
        )
          return focusOnDayElem(c);
      }
    }
  }
  instance.changeMonth(loopDelta);
  focusOnDay(getFirstAvailableDay(loopDelta, instance), 0, instance);
  return undefined;
}

function focusOnDay(
  current: DayElement | undefined,
  offset: number,
  instance: flatpickr.Instance
) {
  const dayFocused = isInView(
    document.activeElement || document.body,
    instance
  );

  let startElem;
  if (current !== undefined) {
    startElem = current;
  } else {
    if (dayFocused) {
      startElem = document.activeElement as DayElement;
    } else {
      if (
        instance.selectedDateElem !== undefined &&
        isInView(instance.selectedDateElem, instance)
      ) {
        startElem = instance.selectedDateElem;
      } else {
        if (
          instance.todayDateElem !== undefined &&
          isInView(instance.todayDateElem, instance)
        ) {
          startElem = instance.todayDateElem;
        } else {
          startElem = getFirstAvailableDay(offset > 0 ? 1 : -1, instance);
        }
      }
    }
  }

  if (startElem === undefined) {
    instance._input.focus();
  } else if (!dayFocused) {
    focusOnDayElem(startElem);
  } else {
    getNextAvailableDay(startElem, offset, instance);
  }
}
